import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// An upstream proxy caps each request body at 1 MB. Files below SINGLE_MAX go
// in a single request; larger files are sent as CHUNK_SIZE pieces (each well
// under 1 MB) and reassembled by the media service.
const SINGLE_MAX = 900 * 1024;
const CHUNK_SIZE = 800 * 1024;

const genUploadId = () => {
    const raw = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return raw.replace(/[^a-zA-Z0-9_-]/g, '');
};

export const mediaApi = createApi({
    reducerPath: 'mediaApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_MEDIA_SERVICE_URL,
    }),
    endpoints: (builder) => ({
        uploadMedia: builder.mutation({
            queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
                const file = arg instanceof File ? arg : arg.file;
                let userId = arg.userId;

                if (!userId) {
                    const state = getState();
                    userId = state.user?.user?.id || state.user?.user?.user_id || 0;
                }

                // Small files: single request (fast path).
                if (file.size <= SINGLE_MAX) {
                    const formData = new FormData();
                    formData.append('file', file);

                    const result = await baseQuery({
                        url: `/upload?user_id=${userId}`,
                        method: 'POST',
                        body: formData,
                    });

                    if (result.error) {
                        return { error: result.error };
                    }
                    return { data: result.data };
                }

                // Large files: chunked upload, each chunk under the 1 MB limit.
                const uploadId = genUploadId();
                const total = Math.ceil(file.size / CHUNK_SIZE);
                const filename = encodeURIComponent(file.name || 'file');
                const ctype = encodeURIComponent(file.type || 'application/octet-stream');

                let lastData;
                for (let index = 0; index < total; index += 1) {
                    const start = index * CHUNK_SIZE;
                    const blob = file.slice(start, start + CHUNK_SIZE);
                    const url =
                        `/upload/chunk?upload_id=${uploadId}&index=${index}&total=${total}` +
                        `&user_id=${userId}&filename=${filename}&content_type=${ctype}`;

                    // eslint-disable-next-line no-await-in-loop
                    const result = await baseQuery({
                        url,
                        method: 'POST',
                        body: blob,
                        headers: { 'Content-Type': 'application/octet-stream' },
                    });

                    if (result.error) {
                        return { error: result.error };
                    }
                    lastData = result.data;
                }

                return { data: lastData };
            }
        })
    })
});

export const {
    useUploadMediaMutation
} = mediaApi;
